import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { LineChart } from "@mui/x-charts/LineChart";
import DarkBarChart from "../graphComponents/BarChart.js";
import { fetchHomeInfo } from "../data/home.js";
import { fetchRockets } from "../data/rocketFamilies.js";
import { fetchAdditionalGraphInfo } from "../data/graphData.js";
import { fetchLaunchesOnly } from "../data/launches.js";
import { fetchPads } from "../data/pads.js";
import { fetchStageInfo, fetchStages } from "../data/stages.js";
import { getLaunchChartColor } from "../home/Home.js";
import {
  buildReuseChart,
  buildTimelineRowFromFlights,
  extractFlightDates,
  getDaysBetween,
  statusIsActive,
} from "../graphComponents/reuseTimeline.js";
import "./ShareChart.css";

/*
 ShareChart renders a single chart as a fixed-width, screenshot-ready card.
 It exists for the automated X posts: the backend loads /share/:chartKey in a
 headless browser and screenshots #share-card, so posted images are identical
 to a chart rendered by the site's own components.

 Glance charts (used by post triggers — designed to tell one story fast):
   /share/year-race            ?highlight=2026&years=3
   /share/booster-leaderboard  ?highlight=B1067&rows=10&family=falcon
   /share/turnaround-trend     ?type=pad|stage&id=123&last=15
   /share/milestone-climb      ?step=100
   /share/launch-gap           ?last=20

 Glance charts (manual for now — recap-post candidates):
   /share/mass-race            ?years=3
   /share/monthly-cadence
   /share/flight-distribution  ?family=falcon
   /share/starlink-share

 Site charts (kept for manual use, not attached automatically):
   /share/launches-per-year    ?highlight=2026
   /share/mass-per-year
   /share/booster-reuse        ?highlight=B1067&rows=12

 Common params: width (default 1200). The card sets data-share-ready="true"
 once its data has rendered; the screenshot task waits on that attribute.
*/

const CHART_META = {
  "year-race": { label: "Launch cadence", title: "Launches: race through the year" },
  "booster-leaderboard": { label: "Reuse", title: "Most flown boosters" },
  "turnaround-trend": { label: "Turnaround", title: "Turnaround trend" },
  "milestone-climb": { label: "Milestones", title: "The climb: every SpaceX launch" },
  "launch-gap": { label: "Cadence", title: "Days between SpaceX launches" },
  "mass-race": { label: "Payload", title: "Mass to orbit: race through the year" },
  "monthly-cadence": { label: "Cadence", title: "Launches per month" },
  "flight-distribution": { label: "Reuse", title: "The fleet, by flight count" },
  "starlink-share": { label: "Payload", title: "Starlink vs everything else" },
  "launches-per-year": { label: "Launch cadence", title: "Launches per year" },
  "mass-per-year": { label: "Payload", title: "Estimated mass to orbit (tonnes)" },
  "booster-reuse": { label: "Reuse", title: "Most flown Falcon boosters" },
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Site palette (see :root in App.css): faint -> violet -> cyan, oldest year to current.
// Record/highlight green (--bt-green) lives in ShareChart.css.
const YEAR_COLORS = ["#5f6979", "#8067d8", "#238bb8"];

const getDayOfYear = (date) => {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  return Math.floor((date.valueOf() - start) / 86400000);
};
const formatDaysShort = (days) => (days >= 10 ? `${Math.round(days)}d` : `${Math.round(days * 10) / 10}d`);

const darkTheme = createTheme({ palette: { mode: "dark" } });

const ShareChart = () => {
  const { chartKey } = useParams();
  const [searchParams] = useSearchParams();

  const width = Math.max(600, Math.min(Number(searchParams.get("width")) || 1200, 1600));
  const highlight = searchParams.get("highlight") || "";
  const rowCount = Math.max(5, Math.min(Number(searchParams.get("rows")) || 10, 20));
  const trendType = searchParams.get("type") || "pad";
  const trendId = searchParams.get("id") || "";
  const trendLast = Math.max(5, Math.min(Number(searchParams.get("last")) || 15, 30));
  const milestoneStep = Math.max(25, Number(searchParams.get("step")) || 100);
  const raceYears = Math.max(2, Math.min(Number(searchParams.get("years")) || 3, 5));
  const family = searchParams.get("family") || "falcon";

  const [ready, setReady] = useState(false);
  const [barData, setBarData] = useState(null);
  const [lineData, setLineData] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [trendBars, setTrendBars] = useState(null);
  const [reuseRows, setReuseRows] = useState(null);
  const [subtitle, setSubtitle] = useState("");
  const [titleOverride, setTitleOverride] = useState("");

  const meta = CHART_META[chartKey];

  useEffect(() => {
    if (!meta) {
      return;
    }

    /* ------------------------- glance charts ------------------------- */

    const loadYearRace = async () => {
      const now = new Date();
      const currentYear = now.getUTCFullYear();
      const launches = (await fetchLaunchesOnly({})).filter((launch) => new Date(launch.time) <= now);

      const doysByYear = {};
      launches.forEach((launch) => {
        const date = new Date(launch.time);
        const year = date.getUTCFullYear();
        (doysByYear[year] = doysByYear[year] || []).push(getDayOfYear(date));
      });

      const years = Array.from({ length: raceYears }, (_, index) => currentYear - raceYears + 1 + index);
      const todayDoy = getDayOfYear(now);

      const series = years.map((year, index) => {
        const doys = (doysByYear[year] || []).sort((a, b) => a - b);
        const lastDay = year === currentYear ? todayDoy : 366;
        const data = [];
        for (let day = 1; day <= 366; day++) {
          data.push(day <= lastDay ? doys.filter((doy) => doy <= day).length : null);
        }
        const isCurrent = year === currentYear;
        return {
          data,
          label: String(year),
          color: isCurrent ? YEAR_COLORS[YEAR_COLORS.length - 1] : YEAR_COLORS[index % (YEAR_COLORS.length - 1)],
          showMark: false,
          curve: "linear",
        };
      });

      // Subtitle: how far ahead of last year's pace the current year is
      const currentCount = (doysByYear[currentYear] || []).length;
      const prevDoys = (doysByYear[currentYear - 1] || []).sort((a, b) => a - b);
      if (currentCount > 0 && prevDoys.length >= currentCount) {
        const prevDayAtSameCount = prevDoys[currentCount - 1];
        const daysAhead = prevDayAtSameCount - todayDoy;
        if (daysAhead > 0) {
          setSubtitle(`${currentCount} launches in ${currentYear} — ${daysAhead} days ahead of ${currentYear - 1}'s pace`);
        } else {
          setSubtitle(`${currentCount} launches in ${currentYear}`);
        }
      } else if (currentCount > 0 && prevDoys.length > 0) {
        setSubtitle(`${currentCount} launches in ${currentYear} — already past ${currentYear - 1}'s full-year total`);
      }

      setLineData({
        series,
        xAxis: [
          {
            data: Array.from({ length: 366 }, (_, index) => index + 1),
            label: "day of year",
            valueFormatter: (value) => String(value),
          },
        ],
        legend: years.map((year, index) => ({
          label: String(year),
          color:
            year === currentYear ? YEAR_COLORS[YEAR_COLORS.length - 1] : YEAR_COLORS[index % (YEAR_COLORS.length - 1)],
        })),
      });
    };

    const loadBoosterLeaderboard = async () => {
      const stageData = await fetchStages("", null, family, "BOOSTER");
      const boosters = (stageData.stages || [])
        .filter((stage) => Number(stage.num_launches) > 0)
        .sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))
        .slice(0, rowCount);

      const max = Math.max(1, ...boosters.map((stage) => Number(stage.num_launches || 0)));
      setLeaderboard(
        boosters.map((stage) => ({
          name: stage.name,
          count: Number(stage.num_launches || 0),
          pct: (Number(stage.num_launches || 0) / max) * 100,
          active: statusIsActive(stage.status),
          highlighted: Boolean(highlight) && stage.name === highlight,
        }))
      );

      if (highlight) {
        const record = boosters.find((stage) => stage.name === highlight);
        if (record) {
          setSubtitle(`${record.name}: ${record.num_launches} flights — the most of any booster`);
        }
      }
    };

    const getLaunchPadId = (launch) =>
      launch && typeof launch.pad === "object" && launch.pad !== null ? launch.pad.id : launch?.pad;

    const loadTurnaroundTrend = async () => {
      let entries = [];
      let vehicleName = "";

      if (trendType === "stage") {
        let stageId = trendId;
        if (!stageId) {
          // No id given: default to the most-flown active booster
          const stageData = await fetchStages("", null, family, "BOOSTER");
          const top = (stageData.stages || [])
            .filter((stage) => Number(stage.num_launches) > 1)
            .sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))[0];
          stageId = top?.id;
        }
        if (stageId) {
          const detail = await fetchStageInfo(stageId);
          vehicleName = detail?.stage?.name || "";
          entries = (detail?.stage_and_recoveries || [])
            .filter((reuse) => reuse.stage_turnaround && reuse.launch?.time)
            .map((reuse) => ({
              time: new Date(reuse.launch.time),
              days: reuse.stage_turnaround / 86400,
              launchName: reuse.launch.name,
            }));
        }
      } else {
        const [launches, pads] = await Promise.all([fetchLaunchesOnly({}), fetchPads("", {})]);
        const now = new Date();
        let padId = trendId;
        if (!padId) {
          // No id given: default to the pad of the most recent launch
          const latest = launches
            .filter((launch) => new Date(launch.time) <= now && getLaunchPadId(launch))
            .sort((a, b) => new Date(b.time) - new Date(a.time))[0];
          padId = getLaunchPadId(latest);
        }
        const pad = (pads || []).find((item) => String(item.id) === String(padId));
        vehicleName = pad?.nickname || pad?.name || "";
        entries = launches
          .filter((launch) => String(getLaunchPadId(launch)) === String(padId) && launch.pad_turnaround)
          .map((launch) => ({
            time: new Date(launch.time),
            days: launch.pad_turnaround / 86400,
            launchName: launch.name,
          }));
      }

      entries.sort((a, b) => a.time - b.time);
      const recent = entries.slice(-trendLast);
      if (!recent.length) {
        setTrendBars([]);
        return;
      }

      const maxDays = Math.max(...recent.map((entry) => entry.days));
      const minDays = Math.min(...recent.map((entry) => entry.days));
      const recordIndex = recent.map((entry) => entry.days).lastIndexOf(minDays);

      setTrendBars(
        recent.map((entry, index) => ({
          days: entry.days,
          pct: Math.max(4, (entry.days / maxDays) * 100),
          record: index === recordIndex,
          label: formatDaysShort(entry.days),
        }))
      );

      if (vehicleName) {
        setTitleOverride(`${vehicleName} turnaround, last ${recent.length} flights`);
        setSubtitle(`New record: ${formatDaysShort(minDays)} — ${recent[recordIndex] === recent[recent.length - 1] ? "set on the latest launch" : "the fastest of the bunch"}`);
      }
    };

    const loadMilestoneClimb = async () => {
      const now = new Date();
      const launches = (await fetchLaunchesOnly({}))
        .filter((launch) => new Date(launch.time) <= now)
        .sort((a, b) => new Date(a.time) - new Date(b.time));

      if (!launches.length) {
        return;
      }

      const dates = launches.map((launch) => new Date(launch.time));
      const counts = launches.map((_, index) => index + 1);
      const total = counts[counts.length - 1];

      setLineData({
        series: [
          {
            data: counts,
            label: "launches",
            color: "#238bb8",
            curve: "linear",
            showMark: ({ index }) => (index + 1) % milestoneStep === 0,
          },
        ],
        xAxis: [
          {
            data: dates,
            scaleType: "time",
            valueFormatter: (value) => String(new Date(value).getUTCFullYear()),
          },
        ],
        legend: null,
      });

      // Subtitle: how fast the last full milestone block went
      const lastMilestone = Math.floor(total / milestoneStep) * milestoneStep;
      if (lastMilestone >= 2 * milestoneStep) {
        const spanDays = getDaysBetween(dates[lastMilestone - milestoneStep - 1], dates[lastMilestone - 1]);
        setSubtitle(
          `${total} launches to date — launches ${lastMilestone - milestoneStep} to ${lastMilestone} took just ${spanDays} days`
        );
      } else {
        setSubtitle(`${total} launches to date`);
      }
    };

    const loadLaunchGap = async () => {
      const now = new Date();
      const launches = (await fetchLaunchesOnly({}))
        .filter((launch) => new Date(launch.time) <= now)
        .sort((a, b) => new Date(a.time) - new Date(b.time));

      const recent = launches
        .slice(-Math.max(trendLast, 15))
        .filter((launch) => launch.company_turnaround)
        .map((launch) => launch.company_turnaround / 86400);

      if (!recent.length) {
        setTrendBars([]);
        return;
      }

      const maxDays = Math.max(...recent);
      const minDays = Math.min(...recent);
      const recordIndex = recent.lastIndexOf(minDays);
      const avg = recent.reduce((total, days) => total + days, 0) / recent.length;

      setTrendBars(
        recent.map((days, index) => ({
          days,
          pct: Math.max(4, (days / maxDays) * 100),
          record: index === recordIndex,
          label: formatDaysShort(days),
        }))
      );
      setSubtitle(
        `Last ${recent.length} launches: one every ${Math.round(avg * 10) / 10} days on average — shortest gap ${formatDaysShort(minDays)}`
      );
    };

    const loadMassRace = async () => {
      const now = new Date();
      const currentYear = now.getUTCFullYear();
      const launches = (await fetchLaunchesOnly({})).filter(
        (launch) => new Date(launch.time) <= now && Number(launch.mass) > 0
      );

      const byYear = {};
      launches.forEach((launch) => {
        const date = new Date(launch.time);
        const year = date.getUTCFullYear();
        (byYear[year] = byYear[year] || []).push({ doy: getDayOfYear(date), tonnes: Number(launch.mass) / 1000 });
      });

      const years = Array.from({ length: raceYears }, (_, index) => currentYear - raceYears + 1 + index);
      const todayDoy = getDayOfYear(now);

      const series = years.map((year, index) => {
        const entries = (byYear[year] || []).sort((a, b) => a.doy - b.doy);
        const lastDay = year === currentYear ? todayDoy : 366;
        const data = [];
        for (let day = 1; day <= 366; day++) {
          if (day > lastDay) {
            data.push(null);
          } else {
            data.push(
              Math.round(entries.filter((entry) => entry.doy <= day).reduce((total, entry) => total + entry.tonnes, 0))
            );
          }
        }
        const isCurrent = year === currentYear;
        return {
          data,
          label: String(year),
          color: isCurrent ? YEAR_COLORS[YEAR_COLORS.length - 1] : YEAR_COLORS[index % (YEAR_COLORS.length - 1)],
          showMark: false,
          curve: "linear",
        };
      });

      const currentTotal = Math.round((byYear[currentYear] || []).reduce((total, entry) => total + entry.tonnes, 0));
      const prevAtSameDay = Math.round(
        (byYear[currentYear - 1] || [])
          .filter((entry) => entry.doy <= todayDoy)
          .reduce((total, entry) => total + entry.tonnes, 0)
      );
      if (currentTotal > 0) {
        setSubtitle(
          prevAtSameDay > 0
            ? `${currentTotal.toLocaleString()} t to orbit in ${currentYear} — vs ${prevAtSameDay.toLocaleString()} t by this day in ${currentYear - 1}`
            : `${currentTotal.toLocaleString()} t to orbit in ${currentYear}`
        );
      }

      setLineData({
        series,
        xAxis: [
          {
            data: Array.from({ length: 366 }, (_, index) => index + 1),
            label: "day of year",
            valueFormatter: (value) => String(value),
          },
        ],
        legend: years.map((year, index) => ({
          label: String(year),
          color:
            year === currentYear ? YEAR_COLORS[YEAR_COLORS.length - 1] : YEAR_COLORS[index % (YEAR_COLORS.length - 1)],
        })),
      });
    };

    const loadMonthlyCadence = async () => {
      const now = new Date();
      const launches = (await fetchLaunchesOnly({})).filter((launch) => new Date(launch.time) <= now);

      // Count launches per calendar month across all history
      const counts = {};
      launches.forEach((launch) => {
        const date = new Date(launch.time);
        const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
        counts[key] = (counts[key] || 0) + 1;
      });

      // Best month ever (for the subtitle and highlight)
      let bestKey = null;
      Object.entries(counts).forEach(([key, count]) => {
        if (!bestKey || count > counts[bestKey]) {
          bestKey = key;
        }
      });

      // Last 12 months, oldest first
      const months = [];
      for (let offset = 11; offset >= 0; offset--) {
        const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1));
        months.push({ key: `${date.getUTCFullYear()}-${date.getUTCMonth()}`, month: date.getUTCMonth() });
      }

      const max = Math.max(1, ...months.map((entry) => counts[entry.key] || 0));
      setTrendBars(
        months.map((entry) => ({
          pct: Math.max(4, ((counts[entry.key] || 0) / max) * 100),
          record: entry.key === bestKey,
          label: String(counts[entry.key] || 0),
          sub: MONTH_NAMES[entry.month],
        }))
      );

      if (bestKey) {
        const [bestYear, bestMonth] = bestKey.split("-").map(Number);
        const inWindow = months.some((entry) => entry.key === bestKey);
        setSubtitle(
          inWindow
            ? `Best month ever: ${MONTH_NAMES[bestMonth]} ${bestYear} with ${counts[bestKey]} launches`
            : `Record to beat: ${counts[bestKey]} launches (${MONTH_NAMES[bestMonth]} ${bestYear})`
        );
      }
    };

    const loadFlightDistribution = async () => {
      const stageData = await fetchStages("", null, family, "BOOSTER");
      const flightCounts = (stageData.stages || [])
        .map((stage) => Number(stage.num_launches) || 0)
        .filter((count) => count > 0);

      const buckets = [
        { label: "1", test: (count) => count === 1 },
        { label: "2-4", test: (count) => count >= 2 && count <= 4 },
        { label: "5-9", test: (count) => count >= 5 && count <= 9 },
        { label: "10-14", test: (count) => count >= 10 && count <= 14 },
        { label: "15-19", test: (count) => count >= 15 && count <= 19 },
        { label: "20+", test: (count) => count >= 20 },
      ];

      const bucketCounts = buckets.map((bucket) => flightCounts.filter(bucket.test).length);
      const max = Math.max(1, ...bucketCounts);

      setTrendBars(
        buckets.map((bucket, index) => ({
          pct: Math.max(4, (bucketCounts[index] / max) * 100),
          record: bucket.label === "20+" && bucketCounts[index] > 0,
          label: String(bucketCounts[index]),
          sub: bucket.label,
        }))
      );

      const veterans = flightCounts.filter((count) => count >= 10).length;
      setSubtitle(
        `${flightCounts.length} boosters have flown — ${veterans} of them 10+ times. Reuse is the norm, not the exception`
      );
    };

    const loadStarlinkShare = async () => {
      const now = new Date();
      const currentYear = now.getUTCFullYear();
      const isStarlink = (launch) =>
        `${launch.name || ""} ${launch.customer || ""}`.toLowerCase().includes("starlink");

      const allLaunches = (await fetchLaunchesOnly({})).filter((launch) => new Date(launch.time) <= now);
      const starlinkLaunches = allLaunches
        .filter(isStarlink)
        .sort((a, b) => new Date(a.time) - new Date(b.time));

      if (!starlinkLaunches.length) {
        setSubtitle("No Starlink launches found");
        return;
      }

      // Prefer cumulative satellite counts; fall back to cumulative launches
      // if the API has counts for too few launches to be meaningful.
      const withCounts = starlinkLaunches.filter((launch) => Number(launch.starlink_sats_count) > 0);
      const useSats = withCounts.length >= starlinkLaunches.length / 2;

      const dates = [];
      const cumulative = [];
      let running = 0;
      starlinkLaunches.forEach((launch) => {
        running += useSats ? Number(launch.starlink_sats_count) || 0 : 1;
        dates.push(new Date(launch.time));
        cumulative.push(running);
      });

      setLineData({
        series: [
          {
            data: cumulative,
            label: useSats ? "satellites" : "launches",
            color: "#238bb8",
            curve: "linear",
            showMark: false,
            area: true,
          },
        ],
        xAxis: [
          {
            data: dates,
            scaleType: "time",
            valueFormatter: (value) => String(new Date(value).getUTCFullYear()),
          },
        ],
        legend: null,
      });

      setTitleOverride(useSats ? "Starlink satellites launched" : "Starlink launches");
      const total = cumulative[cumulative.length - 1];
      const thisYear = allLaunches.filter((launch) => new Date(launch.time).getUTCFullYear() === currentYear);
      const starlinkThisYear = thisYear.filter(isStarlink).length;
      const shareNote = thisYear.length
        ? ` — ${Math.round((starlinkThisYear / thisYear.length) * 100)}% of ${currentYear}'s launches carry Starlink`
        : "";
      setSubtitle(
        useSats
          ? `${total.toLocaleString()} satellites across ${starlinkLaunches.length} launches${shareNote}`
          : `${total.toLocaleString()} Starlink launches to date${shareNote}`
      );
    };

    /* ---------------- site charts (manual use only) ------------------ */

    const loadLaunchesPerYear = async () => {
      const [rockets, homeData] = await Promise.all([
        fetchRockets(),
        fetchHomeInfo(new Date(new Date().getUTCFullYear() - 2, 0, 1), "exponential"),
      ]);

      const seriesInputData = [];
      let totalLaunchesPerYear = [];
      const years = homeData["years"];
      for (const [key, value] of Object.entries(homeData["launches_per_rocket_per_year"])) {
        const launches = JSON.parse(value.replace(/'/g, '"'));

        if (totalLaunchesPerYear.length === 0) {
          totalLaunchesPerYear = [...launches];
        } else {
          totalLaunchesPerYear = totalLaunchesPerYear.map((launch, index) => launch + launches[index]);
        }

        const rocket = rockets.find((item) => String(item.id) === key);
        seriesInputData.push({
          data: launches,
          label: rocket?.name || `Rocket ${key}`,
          area: false,
          stack: "total",
          curve: "linear",
          color: getLaunchChartColor(rocket, seriesInputData.length),
        });
      }

      const launchesPerYear = {};
      for (const [index, value] of Object.entries(totalLaunchesPerYear)) {
        launchesPerYear[years[index]] = value;
      }

      if (highlight && launchesPerYear[highlight] !== undefined) {
        setSubtitle(`${highlight}: ${launchesPerYear[highlight]} launches and counting`);
      }

      setBarData({
        seriesInput: seriesInputData,
        xAxisInput: [{ data: Object.keys(launchesPerYear), type: "linear", scaleType: "band" }],
        showLegend: true,
        totalLaunches: launchesPerYear,
      });
    };

    const loadMassPerYear = async () => {
      const graphData = await fetchAdditionalGraphInfo();
      const massPerYear = graphData.mass_per_year;

      setBarData({
        seriesInput: [
          {
            data: Object.values(massPerYear).map(Number),
            area: false,
            stack: "total",
            curve: "linear",
            color: "#238bb8",
            valueFormatter: (value) => `${value.toLocaleString()} tonnes`,
          },
        ],
        xAxisInput: [{ data: Object.keys(massPerYear).map(Number), type: "linear", scaleType: "band" }],
        showLegend: false,
        totalLaunches: null,
      });
    };

    const loadBoosterReuse = async () => {
      const stageData = await fetchStages("", null, "falcon", "BOOSTER");
      const boosters = (stageData.stages || [])
        .filter((stage) => Number(stage.num_launches) > 0)
        .sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))
        .slice(0, rowCount);

      const details = await Promise.all(
        boosters.map(async (stage) => {
          try {
            const detail = await fetchStageInfo(stage.id);
            const flights = extractFlightDates(detail.stage_and_recoveries);

            if (!flights.length) {
              return null;
            }

            return buildTimelineRowFromFlights({
              id: stage.id,
              name: stage.name,
              active: statusIsActive(stage.status),
              flights,
              count: Number(stage.num_launches || flights.length),
            });
          } catch (error) {
            return null;
          }
        })
      );

      setReuseRows(details.filter(Boolean));
    };

    const loaders = {
      "year-race": loadYearRace,
      "booster-leaderboard": loadBoosterLeaderboard,
      "turnaround-trend": loadTurnaroundTrend,
      "milestone-climb": loadMilestoneClimb,
      "launch-gap": loadLaunchGap,
      "mass-race": loadMassRace,
      "monthly-cadence": loadMonthlyCadence,
      "flight-distribution": loadFlightDistribution,
      "starlink-share": loadStarlinkShare,
      "launches-per-year": loadLaunchesPerYear,
      "mass-per-year": loadMassPerYear,
      "booster-reuse": loadBoosterReuse,
    };

    const load = async () => {
      try {
        await loaders[chartKey]();
      } finally {
        // Two frames so the chart has painted before the screenshot fires
        requestAnimationFrame(() => requestAnimationFrame(() => setReady(true)));
      }
    };

    load();
    // eslint-disable-next-line
  }, [chartKey]);

  const reuseChart = useMemo(() => (reuseRows ? buildReuseChart(reuseRows, "relative") : null), [reuseRows]);

  if (!meta) {
    return <div className="share-error">Unknown chart: {chartKey}</div>;
  }

  return (
    <div className="share-page">
      <div id="share-card" className="share-card" style={{ width }} data-share-ready={ready ? "true" : "false"}>
        <div className="share-head">
          <div>
            <p className="share-label">{meta.label}</p>
            <h1 className="share-title">{titleOverride || meta.title}</h1>
            {subtitle ? <p className="share-subtitle">{subtitle}</p> : null}
          </div>
          <div className="share-brand">
            <img src="/booster-tracker.png" alt="" aria-hidden="true" />
            <span>boostertracker.com</span>
          </div>
        </div>

        {lineData ? (
          <div className="share-chart">
            {lineData.legend ? (
              <div className="share-legend">
                {lineData.legend.map((item) => (
                  <span key={item.label} className="share-legend-item">
                    <i style={{ background: item.color }}></i>
                    {item.label}
                  </span>
                ))}
              </div>
            ) : null}
            <ThemeProvider theme={darkTheme}>
              <LineChart
                series={lineData.series}
                xAxis={lineData.xAxis}
                width={width - 80}
                height={520}
                skipAnimation
                slotProps={{ legend: { hidden: true } }}
              />
            </ThemeProvider>
          </div>
        ) : null}

        {leaderboard ? (
          <div className="share-leaderboard">
            {leaderboard.map((row) => (
              <div key={row.name} className={`share-lb-row ${row.highlighted ? "share-lb-highlight" : ""}`}>
                <span className="share-lb-name">{row.name}</span>
                <span className="share-lb-track">
                  <span className="share-lb-bar" style={{ width: `${row.pct}%` }}></span>
                </span>
                <span className="share-lb-count">{row.count}</span>
              </div>
            ))}
          </div>
        ) : null}

        {trendBars ? (
          trendBars.length ? (
            <div className="share-trend">
              {trendBars.map((bar, index) => (
                <div key={index} className={`share-trend-col ${bar.record ? "share-trend-record" : ""}`}>
                  <span className="share-trend-value">{bar.label}</span>
                  <span className="share-trend-bar" style={{ height: `${bar.pct}%` }}></span>
                  {bar.sub ? <span className="share-trend-sub">{bar.sub}</span> : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="share-error">
              No turnaround data found (type={trendType}
              {trendId ? `, id=${trendId}` : ", no id given — auto-pick found nothing"}). Try
              ?type=pad&id=&lt;padId&gt; or ?type=stage&id=&lt;stageId&gt;.
            </div>
          )
        ) : null}

        {barData ? (
          <div className="share-chart">
            <DarkBarChart
              seriesInput={barData.seriesInput}
              xAxisInput={barData.xAxisInput}
              showLegend={barData.showLegend}
              chartWidth={width - 80}
              totalLaunches={barData.totalLaunches}
            />
          </div>
        ) : null}

        {reuseChart ? (
          <div className="bt-reuse-chart share-reuse">
            <div className="bt-reuse-inner">
              {reuseChart.rows.map((booster) => (
                <div
                  className={`bt-reuse-row ${booster.active ? "active" : "retired"} ${
                    highlight && booster.name === highlight ? "share-highlight" : ""
                  }`}
                  style={{ "--last-x": booster.dots[booster.dots.length - 1]?.x || 0 }}
                  key={booster.id}
                >
                  <span className="bt-reuse-name">{booster.name}</span>
                  <span className="bt-reuse-lane"></span>
                  {booster.dots.map((dot, index) => (
                    <i className="bt-reuse-dot" style={{ "--x": dot.x }} key={`${booster.id}-${index}`}></i>
                  ))}
                  <span className="bt-reuse-count" style={{ "--x": booster.dots[booster.dots.length - 1]?.x || 0 }}>
                    x{booster.count}
                  </span>
                </div>
              ))}
              <div className="bt-reuse-axis">
                {reuseChart.axisLabels.map((label) => (
                  <span key={label}>{label}</span>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ShareChart;
